import React, { useState, useEffect, useRef } from 'react';

const SKETCH_MESSAGES: {
  [key: string]: {
    initMessage: string;
    beforeFirstVertexMessage: string;
    afterFirstVertexMessage: string;
  };
} = {
  polygon: {
    initMessage: 'Click to start drawing',
    beforeFirstVertexMessage: 'Click to continue drawing',
    afterFirstVertexMessage:
      'Click to continue drawing or double-click to complete'
  },
  circle: {
    initMessage: 'Click and drag to draw, release to finish',
    beforeFirstVertexMessage: 'Click and drag to draw, release to finish',
    afterFirstVertexMessage: 'Click and drag to draw, release to finish'
  },
  polyline: {
    initMessage: 'Click to start drawing',
    beforeFirstVertexMessage:
      'Click to continue drawing or double-click to complete',
    afterFirstVertexMessage:
      'Click to continue drawing or double-click to complete'
  }
};

interface Props {
  /** The ArcGIS MapView — used for pointer-move tracking */
  view: any;
  /** The SketchViewModel — used for sketch state events */
  sketchView: any;
  /** Current drawing tool type (polygon | polyline | circle) */
  sketchToolType: string;
}

/**
 * Cursor-following tooltip that guides users through the drawing process.
 * Uses the same messages as the shared DrawingTooltip component but positions
 * itself relative to the map container instead of the page layout, so it
 * works correctly inside the member lookup wizard step.
 *
 * WHY a separate component instead of reusing components/DrawingTooltip:
 * The shared version hardcodes pixel offsets that assume the lodge-enquiry
 * page layout (sidebar width, header height). Changing those would break
 * the lodge-enquiry page.
 */
const MemberLookupDrawingTooltip: React.FC<Props> = ({
  view,
  sketchView,
  sketchToolType
}) => {
  const [message, setMessage] = useState('');
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [visible, setVisible] = useState(false);
  const toolRef = useRef(sketchToolType);
  const messageRef = useRef(message);

  function updateMessage(text: string) {
    messageRef.current = text;
    setMessage(text);
  }

  useEffect(() => {
    if (sketchToolType && sketchToolType !== 'selector') {
      const msgs = SKETCH_MESSAGES[sketchToolType];
      if (msgs) updateMessage(msgs.initMessage);
      toolRef.current = sketchToolType;
    }
  }, [sketchToolType]);

  /**
   * Listens to the SketchViewModel create event to update the tooltip message
   * at each phase of the drawing workflow.
   */
  useEffect(() => {
    if (!sketchView) return;

    const handle = sketchView.on(
      'create',
      (event: __esri.SketchViewModelCreateEvent) => {
        const msgs = SKETCH_MESSAGES[toolRef.current];
        if (!msgs) return;

        switch (event.state) {
          case 'start':
            updateMessage(msgs.beforeFirstVertexMessage);
            break;
          case 'active':
            if (messageRef.current !== msgs.afterFirstVertexMessage) {
              updateMessage(msgs.afterFirstVertexMessage);
            }
            break;
          case 'complete':
          case 'cancel':
            updateMessage('');
            setVisible(false);
            break;
        }
      }
    );

    return () => handle.remove();
  }, [sketchView]);

  /**
   * Tracks the pointer position within the MapView so the tooltip follows
   * the cursor. Offsets by 20px right / 20px down from the cursor to avoid
   * overlapping the crosshair.
   */
  useEffect(() => {
    if (!view) return;

    const OFFSET_X = 20;
    const OFFSET_Y = 20;

    const moveHandle = view.on(
      'pointer-move',
      (e: __esri.MapViewPointerMoveEvent) => {
        setPosition({ left: e.x + OFFSET_X, top: e.y + OFFSET_Y });
        setVisible(true);
      }
    );

    const leaveHandle = view.on('pointer-leave', () => setVisible(false));
    const enterHandle = view.on('pointer-enter', () => setVisible(true));

    return () => {
      moveHandle.remove();
      leaveHandle.remove();
      enterHandle.remove();
    };
  }, [view]);

  if (!message || !position || !visible) return null;

  return (
    <div
      className="drawing-tooltip byda-member-lookup__drawing-tooltip"
      style={{ left: position.left, top: position.top }}
    >
      <div>{message}</div>
    </div>
  );
};

MemberLookupDrawingTooltip.displayName = 'MemberLookupDrawingTooltip';

export default React.memo(MemberLookupDrawingTooltip);
